import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Info, Plus, Trash2 } from "lucide-react";
import { ContactDTO, emptyContact } from "@/types/contact";

interface ContactsSectionProps {
  contacts: ContactDTO[];
  onChange: (contacts: ContactDTO[]) => void;
  disabled?: boolean;
}

/**
 * The people at the client company, edited as a dynamic list.
 *
 * A company may have one contact or five, and the person who pays is often not
 * the person who booked, so any contact can be flagged to receive the bid and
 * the invoice instead of the client's own address.
 */
export function ContactsSection({
  contacts,
  onChange,
  disabled,
}: ContactsSectionProps) {
  const update = (index: number, patch: Partial<ContactDTO>) => {
    onChange(
      contacts.map((contact, i) =>
        i === index ? { ...contact, ...patch } : contact,
      ),
    );
  };

  /**
   * Stripe invoices exactly one address, so choosing an invoice recipient
   * clears whoever held it before. Behaving like a radio group means the admin
   * cannot create a selection we would silently have to narrow.
   */
  const chooseInvoiceRecipient = (index: number, checked: boolean) => {
    onChange(
      contacts.map((contact, i) => ({
        ...contact,
        receivesInvoice: checked && i === index,
      })),
    );
  };

  const remove = (index: number) => {
    onChange(contacts.filter((_, i) => i !== index));
  };

  const add = () => {
    onChange([...contacts, emptyContact()]);
  };

  const flaggedWithoutEmail = contacts.filter(
    (contact) =>
      (contact.receivesBid || contact.receivesInvoice) &&
      !contact.email?.trim(),
  );

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-lg font-semibold text-foreground">
          Additional Contacts
        </h3>
        <p className="text-sm text-muted-foreground">
          Only a name is required. Any number of contacts can receive the bid;
          the invoice goes to one, because Stripe sends it to a single address.
        </p>
      </div>

      {contacts.length === 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-border bg-secondary/30 p-4">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-foreground" />
          <p className="text-sm text-muted-foreground">
            No extra contacts. The bid and invoice go to the email on the bid
            above.
          </p>
        </div>
      )}

      {contacts.map((contact, index) => (
        <Card
          key={contact.id ?? `new-${index}`}
          // The shared Card is a glowing purple panel, which is too loud for a
          // repeated row inside a form section. Keep a plain outline so several
          // contacts still read as separate cards.
          className="mb-0 border-border shadow-none"
        >
          <CardContent className="space-y-3 p-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor={`contact-${index}-name`}>Name</Label>
                <Input
                  id={`contact-${index}-name`}
                  value={contact.name}
                  disabled={disabled}
                  placeholder="Jane Doe"
                  onChange={(e) => update(index, { name: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`contact-${index}-title`}>Title</Label>
                <Input
                  id={`contact-${index}-title`}
                  value={contact.title ?? ""}
                  disabled={disabled}
                  placeholder="CFO"
                  onChange={(e) => update(index, { title: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`contact-${index}-email`}>Email</Label>
                <Input
                  id={`contact-${index}-email`}
                  type="email"
                  value={contact.email ?? ""}
                  disabled={disabled}
                  placeholder="jane@example.com"
                  onChange={(e) => update(index, { email: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`contact-${index}-phone`}>Phone Number</Label>
                <Input
                  id={`contact-${index}-phone`}
                  value={contact.phoneNumber ?? ""}
                  disabled={disabled}
                  placeholder="(555) 555-5555"
                  onChange={(e) =>
                    update(index, { phoneNumber: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`contact-${index}-receives-bid`}
                    checked={contact.receivesBid}
                    disabled={disabled}
                    onCheckedChange={(checked) =>
                      update(index, { receivesBid: checked === true })
                    }
                  />
                  <Label
                    htmlFor={`contact-${index}-receives-bid`}
                    className="cursor-pointer font-normal"
                  >
                    Receives bid
                  </Label>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`contact-${index}-receives-invoice`}
                    checked={contact.receivesInvoice}
                    disabled={disabled}
                    onCheckedChange={(checked) =>
                      chooseInvoiceRecipient(index, checked === true)
                    }
                  />
                  <Label
                    htmlFor={`contact-${index}-receives-invoice`}
                    className="cursor-pointer font-normal"
                  >
                    Receives invoice
                  </Label>
                </div>
              </div>

              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={disabled}
                onClick={() => remove(index)}
                aria-label={`Remove ${contact.name || "contact"}`}
              >
                <Trash2 className="mr-1.5 h-4 w-4" />
                Remove
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}

      {flaggedWithoutEmail.length > 0 && (
        <p className="text-sm text-destructive" role="alert">
          A contact set to receive the bid or invoice needs an email address.
        </p>
      )}

      <Button
        type="button"
        variant="secondary"
        disabled={disabled}
        onClick={add}
      >
        <Plus className="mr-1.5 h-4 w-4" />
        Add Contact
      </Button>
    </div>
  );
}
